import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getNotifications, markNotificationAsRead, markAllNotificationsAsRead } from '../api/notifications';

const NOTIFICATIONS_MUTED_KEY = 'ticketing_notifications_muted_testing';
const MUTED_CHANGE_EVENT = 'ticketing:notifications-muted-changed';

function readMutedFromStorage() {
  try {
    return localStorage.getItem(NOTIFICATIONS_MUTED_KEY) === '1';
  } catch {
    return false;
  }
}

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);
  const [notificationsMuted, setNotificationsMutedState] = useState(readMutedFromStorage);

  useEffect(() => {
    const sync = () => setNotificationsMutedState(readMutedFromStorage());
    window.addEventListener('storage', sync);
    window.addEventListener(MUTED_CHANGE_EVENT, sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(MUTED_CHANGE_EVENT, sync);
    };
  }, []);

  const setNotificationsMuted = useCallback((muted) => {
    try {
      if (muted) localStorage.setItem(NOTIFICATIONS_MUTED_KEY, '1');
      else localStorage.removeItem(NOTIFICATIONS_MUTED_KEY);
    } catch {
      /* ignore quota / private mode */
    }
    setNotificationsMutedState(!!muted);
    window.dispatchEvent(new Event(MUTED_CHANGE_EVENT));
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async (showLoading = true) => {
    if (!user) return;
    if (notificationsMuted) {
      setNotifications([]);
      setUnreadCount(0);
      if (showLoading) setLoading(false);
      return;
    }

    try {
      if (showLoading) setLoading(true);
      const response = await getNotifications({ limit: 50 });
      if (response.success) {
        const fetchedNotifications = response.data.notifications || [];
        setNotifications(fetchedNotifications);
        setUnreadCount(response.data.unread_count || 0);
        setLastFetch(new Date());
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [user, notificationsMuted]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    if (notificationsMuted) return;
    try {
      const response = await markNotificationAsRead(notificationId);
      if (response.success) {
        setNotifications(prev =>
          prev.map(notif =>
            notif.id === notificationId ? { ...notif, is_read: true } : notif
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, [notificationsMuted]);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    if (notificationsMuted) return;
    try {
      const response = await markAllNotificationsAsRead();
      if (response.success) {
        setNotifications(prev =>
          prev.map(notif => ({ ...notif, is_read: true }))
        );
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  }, [notificationsMuted]);

  useEffect(() => {
    if (notificationsMuted) {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [notificationsMuted]);

  // Initial fetch
  useEffect(() => {
    if (user && !notificationsMuted) {
      fetchNotifications();
    }
  }, [user, notificationsMuted, fetchNotifications]);

  // Auto-polling every 30 seconds
  useEffect(() => {
    if (!user || notificationsMuted) return;

    const interval = setInterval(() => {
      fetchNotifications(false); // Silent fetch (no loading state)
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [user, notificationsMuted, fetchNotifications]);

  const value = {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    lastFetch,
    notificationsMuted,
    setNotificationsMuted
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

